/* eslint-disable no-nested-ternary */
import {
  Avatar,
  Divider,
  Hidden,
  List,
  ListItem,
  ListItemText,
  makeStyles
} from "@material-ui/core";
import { useQuery } from "@redux-requests/react";
import chroma from "chroma-js";
import { React, useRef } from "react";
import { useIntl } from "react-intl";
import { Link as RouterLink, matchPath, useLocation } from "react-router-dom";
import * as types from "../ducks/types";
import { GetAvailableLinks, GetAvailableUserLinks } from "../features/lib";
import useUserMe from "../hooks/useUserMe";
import Flaticon from "./Flaticon";
import PoliciesViewer from "./PoliciesViewer";
import { ReactComponent as MyBadgeSvg } from "../assets/icons/my badge.svg";
import { ReactComponent as MyPresentationSvg } from "../assets/icons/my presentations.svg";
import { ReactComponent as EpostersSvg } from "../assets/icons/eposters.svg";
import { ReactComponent as MeetingSchedSvg } from "../assets/icons/meeting sched.svg";
import { ReactComponent as BadgeScanSvg } from "../assets/icons/badge scan.svg";

const useStyles = makeStyles(theme => ({
  root: {
    display: "flex",
    flexDirection: "column",
    minWidth: theme.spacing(26),
    maxWidth: theme.spacing(26),
    height: "100%",
    overflow: "auto",
    marginRight: theme.spacing(3)
  },
  mainList: {
    flexGrow: 1
  },
  divider: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  item: {
    borderRadius: "24px",
    justifyContent: "flex-start",
    "&:hover": {
      backgroundColor: ({ highlightColor }) =>
        highlightColor ? chroma(highlightColor).alpha(0.05) : "inherit"
    }
  },
  itemSelected: {
    backgroundColor: ({ highlightColor }) =>
      highlightColor ? `${chroma(highlightColor).alpha(0.1)} !important` : "inherit",
    color: ({ highlightColor }) => highlightColor || "inherit"
  },
  smallAvatar: {
    width: theme.spacing(3),
    height: theme.spacing(3)
  },
  icon: {
    width: theme.spacing(3),
    height: theme.spacing(3)
  }
}));

const SideBar = () => {
  const intl = useIntl();
  const { pathname } = useLocation();
  const { user } = useUserMe();
  const {
    data: { data: congress }
  } = useQuery({ type: types.GET_CONGRESS, defaultData: { data: {} } });
  const classes = useStyles(congress);

  const extraLinks = [];
  if (user?.hasRoles(["accademic"])) {
    extraLinks.push({
      label: intl.formatMessage({
        id: "Shared.SideBar.MyPosters",
        defaultMessage: "My Posters"
      }),
      route: "/my-posters",
      icon: EpostersSvg,
      featureName: "eposters"
    });
  }
  if (user?.hasRoles(["speaker"])) {
    extraLinks.push({
      label: intl.formatMessage({
        id: "Shared.SideBar.MyPresentations",
        defaultMessage: "My Presentations"
      }),
      route: "/my-presentations",
      icon: MyPresentationSvg,
      featureName: "presentation"
    });
  }
  if (user?.hasRoles(["expert", "exhibitor"])) {
    extraLinks.push({
      label: intl.formatMessage({
        id: "Shared.SideBar.MeetingScheduler",
        defaultMessage: "Meeting Scheduler"
      }),
      route: "/meeting-scheduler",
      icon: MeetingSchedSvg,
      featureName: "companies"
    });
  }
  (user?.profile?.exhibitors || []).forEach(exhibitor => {
    extraLinks.push({
      label: exhibitor.name,
      route: `/companies/${exhibitor.id}`,
      avatar: exhibitor.logo,
      featureName: "companies"
    });
  });
  if (user?.expert) {
    extraLinks.push({
      label: user.expert.publicName,
      route: `/experts/${user.expert.id}`,
      avatar: user.expert.publicAvatar,
      featureName: "experts"
    });
  }
  if (user?.hasRoles(["assistant"])) {
    extraLinks.push({
      label: intl.formatMessage({ id: "Shared.SideBar.BadgeScan", defaultMessage: "Badge Scan" }),
      icon: BadgeScanSvg,
      route: "/physical_rooms",
      featureName: "my_badge"
    });
  }

  const userExtraLinks = [
    {
      label: intl.formatMessage({ id: "Shared.BaseLayout.Home", defaultMessage: "Home" }),
      icon: "home",
      route: "/home",
      availableUnpublished: false
    }
  ];

  if (congress.enableFeatureMyBadge) {
    userExtraLinks.push({
      label: intl.formatMessage({
        id: "Shared.BaseLayout.MyBadge",
        defaultMessage: "My Badge"
      }),
      icon: MyBadgeSvg,
      route: "/my-badge",
      availableUnpublished: false
    });
  }

  const links = GetAvailableLinks(extraLinks);
  const userLinks = GetAvailableUserLinks(userExtraLinks);

  const policiesViewerRef = useRef({ current: {} });

  if (pathname === "/learn") {
    return null;
  }

  return (
    <div className={classes.root}>
      <List className={classes.mainList}>
        {links.map(({ label, route, icon: Icon, avatar }) => (
          <ListItem
            key={route}
            to={route}
            button
            selected={Boolean(matchPath(pathname, route))}
            component={RouterLink}
            classes={{ root: classes.item, selected: classes.itemSelected }}
          >
            {Icon ? (
              typeof Icon === "string" ? (
                <Flaticon icon={Icon} fontSize="1.25rem" />
              ) : (
                <Icon className={classes.icon} />
              )
            ) : (
              <Avatar variant="rounded" alt={label} src={avatar} className={classes.smallAvatar} />
            )}
            &nbsp;&nbsp;
            <ListItemText primary={<strong>{label}</strong>} />
          </ListItem>
        ))}
        <Divider className={classes.divider} />
        <ListItem
          to="/help-desk"
          button
          selected={Boolean(matchPath(pathname, "/help-desk"))}
          component={RouterLink}
          classes={{ root: classes.item, selected: classes.itemSelected }}
        >
          <Flaticon icon="lifesaver" fontSize="1.25rem" />
          &nbsp;&nbsp;
          <ListItemText
            primary={
              <strong>
                {intl.formatMessage({
                  id: "Shared.SideBar.HelpDesk",
                  defaultMessage: "Help desk"
                })}
              </strong>
            }
          />
        </ListItem>
        <ListItem
          to="/info"
          button
          selected={Boolean(matchPath(pathname, "/info"))}
          component={RouterLink}
          classes={{ root: classes.item, selected: classes.itemSelected }}
        >
          <Flaticon icon="question" fontSize="1.25rem" />
          &nbsp;&nbsp;
          <ListItemText
            primary={
              <strong>
                {congress.infoPageTitle ||
                  intl.formatMessage({
                    id: "Shared.SideBar.Info",
                    defaultMessage: "Info"
                  })}
              </strong>
            }
          />
        </ListItem>
        <Hidden mdUp>
          <Divider className={classes.divider} />
          {userLinks.map(({ icon: Icon, label, route }) => (
            <ListItem
              key={route}
              to={route}
              button
              component={RouterLink}
              classes={{ root: classes.item, selected: classes.itemSelected }}
            >
              {typeof Icon === "string" ? (
                <Flaticon icon={Icon} fontSize="1.25rem" />
              ) : (
                <Icon className={classes.icon} />
              )}
              &nbsp;&nbsp;{label}
            </ListItem>
          ))}
        </Hidden>
      </List>
      <List>
        <Divider className={classes.divider} />
        <ListItem
          button
          classes={{ root: classes.item }}
          onClick={() => {
            policiesViewerRef.current.setOpenPrivacyPolicy(true);
          }}
        >
          <ListItemText primary="Privacy Policy" />
        </ListItem>
        <ListItem
          button
          classes={{ root: classes.item }}
          onClick={() => {
            policiesViewerRef.current.setOpenCookiePolicy(true);
          }}
        >
          <ListItemText primary="Cookie Policy" />
        </ListItem>
      </List>

      <PoliciesViewer policiesViewerRef={policiesViewerRef} />
    </div>
  );
};

export default SideBar;
